/*
 * FashionSense product page (fsb-pdp). One deferred file, loaded by sections/fsb-main-product.liquid, no dependencies.
 * Elements: fsb-pdp-product, -gallery, -lightbox, -video, -modal (size guide), -accordion, -quantity, -share,
 * -sticky-bar, -recommendations, -recently-viewed.
 * Events (bubbling CustomEvents, so they reach document): fsb:pdp:variant-change, fsb:pdp:request-add,
 * fsb:pdp:cart-added, fsb:pdp:cart-error, fsb:pdp:atc-state.
 * Binds only to data-fsb-* hooks, ids and tag names. State is written as attributes (hidden, data-state, aria-*).
 */
(() => {
  'use strict';
  if (window.FsbPdp) return;

  const VERSION = '1.0.0';
  const doc = document;
  const html = doc.documentElement;

  /* ---------------------------------------------------------------- helpers */

  const qs = (scope, sel) => (scope ? scope.querySelector(sel) : null);
  const qsa = (scope, sel) => (scope ? Array.from(scope.querySelectorAll(sel)) : []);
  const kids = (parent, attr) => (parent ? Array.from(parent.children).filter((el) => el.hasAttribute(attr)) : []);
  const define = (name, ctor) => customElements.get(name) || customElements.define(name, ctor);
  const setText = (el, text) => el && el.textContent !== text && (el.textContent = text);
  const setHidden = (el, hide) => el && el.hidden !== hide && (el.hidden = hide);
  const setAttr = (el, name, value) => {
    if (!el) return;
    if (value === null) el.hasAttribute(name) && el.removeAttribute(name);
    else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const mediaId = (el) => el.getAttribute('data-fsb-media-id');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  // Liquid passes [name] placeholders (Shopify's lexer rejects '{' '}' inside {{ }} strings); {name} kept for safety.
  const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}|\[(\w+)\]/g, (m, a, b) => { const k = a || b; return k in map ? String(map[k]) : m; });
  const tidy = (t) => t.replace(/\s+/g, ' ').replace(/(\.\s*){2,}/g, '. ').replace(/^[\s.]+|[\s.]+$/g, '');
  const closestIn = (event, sel) => (event.target && event.target.closest ? event.target.closest(sel) : null);
  const focusEl = (el) => {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  };
  const readJSON = (el) => {
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  };

  // Locale strings arrive HTML-escaped from the t filter: decode once, then only ever use textContent.
  const decoder = doc.createElement('textarea');
  const decode = (v) => {
    if (typeof v !== 'string' || v.indexOf('&') === -1) return typeof v === 'string' ? v : '';
    decoder.innerHTML = v;
    return decoder.value;
  };
  const cache = new WeakMap();
  const rootOf = (el) => (el && el.closest ? el.closest('fsb-pdp-product') : null);
  const stringsFor = (el) => {
    const script = qs(el && el.localName === 'fsb-pdp-product' ? el : rootOf(el), '[data-fsb-pdp-strings]');
    if (!script) return {};
    if (!cache.has(script)) {
      const raw = readJSON(script) || {};
      const out = {};
      Object.keys(raw).forEach((k) => (out[k] = decode(raw[k])));
      cache.set(script, out);
    }
    return cache.get(script);
  };
  const productDataFor = (root) => {
    const script = qs(root, '[data-fsb-product-json]');
    if (script && !cache.has(script)) cache.set(script, readJSON(script));
    return script ? cache.get(script) : null;
  };

  // Reduced motion: one shared query kept current by a change listener; data-fsb-motion="false" also disables motion.
  const reduceQuery = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  let reduceMotion = Boolean(reduceQuery && reduceQuery.matches);
  const motionWatchers = new Set();
  const onMotion = (e) => {
    reduceMotion = e.matches;
    motionWatchers.forEach((fn) => fn(reduceMotion));
  };
  if (reduceQuery) reduceQuery.addEventListener ? reduceQuery.addEventListener('change', onMotion) : reduceQuery.addListener(onMotion);
  const motionOk = (el) => !reduceMotion && !(el && el.closest && el.closest('[data-fsb-motion="false"]'));
  const behaviorFor = (el) => (motionOk(el) ? 'smooth' : 'auto');
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const watchMedia = (query, fn, signal) => {
    const list = matchMedia(query);
    if (list.addEventListener) list.addEventListener('change', fn, { signal });
    else {
      list.addListener(fn);
      signal.addEventListener('abort', () => list.removeListener(fn));
    }
    return list;
  };

  // Dialogs: html.fsb-pdp-lock while any dialog is open (counted), backdrop = a click outside the dialog box.
  let locks = 0;
  const lock = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    html.classList.toggle('fsb-pdp-lock', locks > 0);
  };
  const openDialog = (dialog) => {
    if (dialog.open) return;
    try {
      dialog.showModal();
    } catch (e) {
      dialog.setAttribute('open', '');
    }
  };
  const closeDialog = (dialog) => {
    if (!dialog.open) return;
    if (typeof dialog.close === 'function') return dialog.close();
    dialog.removeAttribute('open');
    dialog.dispatchEvent(new Event('close'));
  };
  const isBackdrop = (dialog, e) => {
    if (e.target !== dialog) return false;
    const r = dialog.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  };

  // Horizontal scroll-snap tracks.
  const scrolls = (track) => track.scrollWidth > track.clientWidth + 1;
  const nearest = (track, slides) => {
    const box = track.getBoundingClientRect();
    const centre = box.left + box.width / 2;
    let best = null;
    let gap = Infinity;
    slides.forEach((slide) => {
      const r = slide.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - centre);
      if (r.width && d < gap) [best, gap] = [slide, d];
    });
    return best;
  };
  const scrollToSlide = (track, slide, behavior) => {
    const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const left = track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left - pad;
    track.scrollTo({ left, behavior });
  };
  // Zoom: let the browser fetch the largest srcset candidate, and map a pointer to a transform-origin.
  const upgraded = new WeakSet();
  const useLargest = (img) => {
    if (!img || upgraded.has(img)) return;
    upgraded.add(img);
    const widths = String(img.getAttribute('srcset') || '').split(',').map((p) => parseInt(p.trim().split(/\s+/)[1], 10)).filter((w) => w > 0);
    if (widths.length) img.sizes = `${Math.max.apply(null, widths)}px`;
  };
  // The image's untransformed box is derived from its current scale and origin, so the pointer maps exactly.
  const zooms = new WeakMap();
  const zoomOf = (img) => zooms.get(img) || { o: { x: 50, y: 50 }, s: 1 };
  const boxOf = (img) => {
    const r = img.getBoundingClientRect();
    const z = zoomOf(img);
    const w = r.width / z.s;
    const h = r.height / z.s;
    return { left: r.left + (z.o.x / 100) * w * (z.s - 1), top: r.top + (z.o.y / 100) * h * (z.s - 1), w, h };
  };
  const originAt = (img, x, y) => {
    const b = boxOf(img);
    return b.w && b.h ? { x: clamp(((x - b.left) / b.w) * 100, 0, 100), y: clamp(((y - b.top) / b.h) * 100, 0, 100) } : { x: 50, y: 50 };
  };
  const zoomTo = (img, o, scale) => {
    const z = zoomOf(img);
    const next = { o: o || z.o, s: scale === undefined ? z.s : scale || 1 };
    img.style.transformOrigin = o ? `${o.x}% ${o.y}%` : '';
    if (scale !== undefined) img.style.transform = scale ? `scale(${scale})` : '';
    if (next.s === 1 && !o) zooms.delete(img);
    else zooms.set(img, next);
  };

  // Dawn integration: cart UI, pubsub and Shopify Standard Events are all optional and guarded.
  const routes = () => window.routes || {};
  const cartUrl = () => routes().cart_url || '/cart';
  const standard = (name) => (window.StandardEvents && window.StandardEvents[name]) || null;
  const publishSafe = (key, data) => {
    try {
      /* global publish, PUB_SUB_EVENTS */
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS[key]) return;
      const result = publish(PUB_SUB_EVENTS[key], data);
      if (result && result.catch) result.catch(() => {});
    } catch (e) {
      /* a subscriber must never break add to cart */
    }
  };
  const cartUI = () => {
    const cart = qs(doc, 'cart-notification') || qs(doc, 'cart-drawer');
    return cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function' ? cart : null;
  };
  const addUrl = (form) => {
    try {
      const url = new URL(routes().cart_add_url || form.getAttribute('action') || '/cart/add', location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
      return url.toString();
    } catch (e) {
      return '/cart/add.js';
    }
  };
  const fetchCart = async (ms) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      const res = await fetch(`${cartUrl()}.js?sections=cart-icon-bubble`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: ctrl.signal });
      return res.ok ? await res.json() : null;
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };
  const inCartCount = (cart, id) =>
    (cart && Array.isArray(cart.items) ? cart.items : []).filter((i) => String(i.variant_id) === String(id)).reduce((n, i) => n + (Number(i.quantity) || 0), 0);
  const renderBubble = (cart) => {
    const markup = cart && cart.sections ? cart.sections['cart-icon-bubble'] : null;
    const bubble = doc.getElementById('cart-icon-bubble');
    const source = typeof markup === 'string' && bubble ? new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section') : null;
    if (source) bubble.innerHTML = source.innerHTML;
    return Boolean(source);
  };
  let bumpTimer = 0;
  const bump = (origin) => {
    const bubble = doc.getElementById('cart-icon-bubble');
    if (!bubble || !motionOk(origin)) return;
    bubble.classList.remove('fsb-pdp-bump');
    void bubble.offsetWidth; // restart the animation
    bubble.classList.add('fsb-pdp-bump');
    clearTimeout(bumpTimer);
    bumpTimer = setTimeout(() => bubble.classList.remove('fsb-pdp-bump'), 600);
  };
  const errorText = (r) => {
    if (!r) return '';
    if (typeof r.description === 'string' && r.description) return r.description;
    if (typeof r.errors === 'string') return r.errors;
    if (r.errors && typeof r.errors === 'object') return Object.keys(r.errors).map((k) => [].concat(r.errors[k]).join(', ')).join(' ');
    return typeof r.message === 'string' ? r.message : '';
  };
  // Shared by the main and sticky add-to-cart buttons. States: idle|loading|success|error|soldout|unavailable.
  const paintButton = (button, label, state, locked, s) => {
    if (!button) return;
    const text = { idle: s.addToCart, error: s.addToCart, loading: s.adding, success: s.added, soldout: s.soldOut, unavailable: s.unavailable }[state];
    setAttr(button, 'data-state', state);
    if (text) setText(label, text);
    const off = state === 'soldout' || state === 'unavailable';
    if (button.disabled !== off) button.disabled = off;
    setAttr(button, 'aria-busy', state === 'loading' ? 'true' : null);
    setAttr(button, 'aria-disabled', state === 'loading' || locked ? 'true' : null);
  };

  /* ---------------------------------------------------------------- base element */

  class Base extends HTMLElement {
    connectedCallback() {
      if (this.ac) return;
      this.ac = new AbortController();
      this.timers = new Set();
      try {
        this.setup();
      } catch (e) {
        console.error('[fsb-pdp]', this.localName, e);
      }
    }

    disconnectedCallback() {
      if (!this.ac) return;
      this.ac.abort();
      this.ac = null;
      this.timers.forEach(clearTimeout);
      this.timers.clear();
      try {
        this.teardown();
      } catch (e) {
        /* nothing left to clean */
      }
    }

    setup() {}

    teardown() {}

    // Listeners are removed through the AbortController; the guard also silences them where `signal` is unsupported.
    on(target, type, fn, options) {
      if (target && this.ac) target.addEventListener(type, (e) => this.ac && fn(e), Object.assign({ signal: this.ac.signal }, options));
    }

    wait(fn, ms) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        fn();
      }, ms);
      this.timers.add(timer);
      return timer;
    }

    stop(timer) {
      clearTimeout(timer);
      if (this.timers) this.timers.delete(timer);
    }

    emit(type, detail) {
      this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
    }
  }

  /* ---------------------------------------------------------------- deferred video / 3D model */

  class FsbPdpVideo extends Base {
    setup() {
      this.on(qs(this, '[data-fsb-video-play]'), 'click', () => this.load());
    }

    load() {
      if (this.player) return this.play();
      const tpl = qs(this, 'template[data-fsb-video-template]');
      const target = qs(this, '[data-fsb-video-target]');
      const source = tpl && tpl.content ? tpl.content.firstElementChild : null;
      if (!source || !target) return;
      qsa(doc, 'fsb-pdp-video').forEach((other) => other !== this && other.pause && other.pause());
      target.appendChild(source.cloneNode(true));
      this.player = qs(target, 'video, iframe, model-viewer');
      this.setAttribute('data-fsb-video-loaded', '');
      setHidden(qs(this, '[data-fsb-video-play]'), true);
      if (!this.player) return;
      if (this.player.localName === 'model-viewer') this.loadModelUI();
      this.play();
      focusEl(this.player); // the play button is now hidden; keep keyboard focus on the player
    }

    loadModelUI() {
      const shopify = window.Shopify;
      const viewer = this.player;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (!errors && shopify.ModelViewerUI && viewer.isConnected) this.modelUI = new shopify.ModelViewerUI(viewer);
          },
        },
      ]);
    }

    play() {
      if (!this.player || this.player.localName !== 'video') return;
      const attempt = this.player.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }

    pause() {
      const p = this.player;
      if (!p) return;
      if (p.localName === 'video') p.pause();
      else if (p.localName === 'iframe' && p.contentWindow) {
        const src = p.getAttribute('src') || '';
        if (src.indexOf('youtube') !== -1) p.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        else if (src.indexOf('vimeo') !== -1) p.contentWindow.postMessage('{"method":"pause"}', '*');
      } else if (this.modelUI && this.modelUI.pause) this.modelUI.pause();
    }
  }

  /* ---------------------------------------------------------------- gallery */

  class FsbPdpGallery extends Base {
    setup() {
      this.track = qs(this, '[data-fsb-gallery-track]');
      this.slides = kids(this.track, 'data-fsb-media-id');
      if (!this.track) return; // no media: placeholder frame only
      this.prev = qs(this, '[data-fsb-gallery-prev]');
      this.next = qs(this, '[data-fsb-gallery-next]');
      this.counter = qs(this, '[data-fsb-gallery-counter]');
      this.dotsWrap = qs(this, '[data-fsb-gallery-dots]');
      this.dots = qsa(this.dotsWrap, '[data-fsb-dot]');
      this.thumbsWrap = qs(this, '[data-fsb-gallery-thumbs]');
      this.thumbItems = kids(this.thumbsWrap, 'data-fsb-media-id');
      this.active = this.slides.find((s) => s.hasAttribute('data-fsb-active') && !s.hidden) || this.visible()[0] || null;
      this.on(this.prev, 'click', () => this.step(-1));
      this.on(this.next, 'click', () => this.step(1));
      this.on(this.thumbsWrap, 'click', (e) => {
        const thumb = closestIn(e, '[data-fsb-thumb]');
        if (thumb && !thumb.hasAttribute('data-fsb-lightbox-open')) this.goToMedia(mediaId(thumb), { behavior: behaviorFor(this), user: true });
      });
      this.on(this.track, 'scroll', () => this.onScroll(), { passive: true });
      this.on(this.track, 'keydown', (e) => this.onKey(e));
      this.setupHoverZoom();
      setAttr(this, 'data-fsb-visible-count', String(this.visible().length));
    }

    teardown() {
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.zoomFrame);
      this.scrollFrame = this.zoomFrame = 0;
      motionWatchers.delete(this.motionFn);
      this.resetZoom();
    }

    visible() {
      return this.slides.filter((s) => !s.hidden);
    }

    getMedia() {
      return this.slides.map((s) => {
        const img = qs(s, 'img');
        return { id: mediaId(s), alt: img ? img.getAttribute('alt') || '' : '' };
      });
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (!scrolls(this.track)) return;
        const slide = nearest(this.track, this.visible());
        if (!slide) return;
        // Ignore intermediate slides while a programmatic smooth scroll is still travelling.
        if (this.pending && slide !== this.pending && performance.now() < this.pendingUntil) return;
        this.pending = null;
        this.setActive(slide);
      });
    }

    onKey(e) {
      if (e.target !== this.track || e.altKey || e.ctrlKey || e.metaKey || !scrolls(this.track)) return;
      const vis = this.visible();
      const opts = { behavior: behaviorFor(this), user: true };
      if (vis.length < 2) return;
      if (e.key === 'ArrowRight') this.step(1);
      else if (e.key === 'ArrowLeft') this.step(-1);
      else if (e.key === 'Home') this.goTo(vis[0], opts);
      else if (e.key === 'End') this.goTo(vis[vis.length - 1], opts);
      else return;
      e.preventDefault();
    }

    // Prev/next wrap around (no auto-advance anywhere).
    step(dir) {
      const vis = this.visible();
      if (vis.length < 2) return;
      const i = Math.max(0, vis.indexOf(this.active));
      this.goTo(vis[(i + dir + vis.length) % vis.length], { behavior: behaviorFor(this), user: true });
    }

    goToMedia(id, options) {
      this.goTo(this.slides.find((s) => mediaId(s) === String(id)), options);
    }

    goTo(slide, options) {
      if (!slide || slide.hidden || !this.track) return;
      const opts = options || {};
      this.setActive(slide);
      if (scrolls(this.track)) {
        this.pending = slide;
        this.pendingUntil = performance.now() + 900;
        scrollToSlide(this.track, slide, opts.behavior || 'auto');
      } else if (opts.user) {
        // Desktop grid / stacked layouts move the page only when the shopper picked an image.
        slide.scrollIntoView({ behavior: opts.behavior || 'auto', block: 'nearest' });
      }
    }

    setActive(slide) {
      if (!slide) return;
      const changed = this.active !== slide;
      const id = mediaId(slide);
      const vis = this.visible();
      let current = null;
      this.active = slide;
      this.slides.forEach((s) => setAttr(s, 'data-fsb-active', s === slide ? '' : null));
      setText(this.counter, `${vis.indexOf(slide) + 1} / ${vis.length}`);
      this.dots.forEach((d) => setAttr(d, 'data-fsb-active', mediaId(d) === id ? '' : null));
      qsa(this.thumbsWrap, '[data-fsb-thumb]').forEach((t) => {
        const on = mediaId(t) === id && !t.closest('[hidden]');
        setAttr(t, 'aria-current', on ? 'true' : 'false');
        if (on) current = t;
      });
      if (!changed) return;
      if (current) this.revealThumb(current);
      this.resetZoom();
      qsa(this.track, 'fsb-pdp-video').forEach((v) => !slide.contains(v) && v.pause && v.pause());
    }

    // Keep the active thumbnail inside its own scroll box without moving the page.
    revealThumb(thumb) {
      const wrap = this.thumbsWrap;
      requestAnimationFrame(() => {
        const box = wrap.getBoundingClientRect();
        const r = thumb.getBoundingClientRect();
        const left = wrap.scrollWidth > wrap.clientWidth + 1 ? Math.min(0, r.left - box.left - 8) || Math.max(0, r.right - box.right + 8) : 0;
        const top = wrap.scrollHeight > wrap.clientHeight + 1 ? Math.min(0, r.top - box.top - 8) || Math.max(0, r.bottom - box.bottom + 8) : 0;
        if (left || top) wrap.scrollBy({ left, top, behavior: behaviorFor(this) });
      });
    }

    // ids: Set of media ids to show, or null to show all. Mirrors the server-side [hidden] filter and relabels.
    applyFilter(ids) {
      if (!this.track) return;
      const show = (el) => !ids || ids.has(String(mediaId(el)));
      let changed = false;
      this.slides.forEach((s) => {
        if (s.hidden === show(s)) {
          s.hidden = !show(s);
          changed = true;
        }
      });
      if (!changed) return;
      this.dots.forEach((d) => setHidden(d, !show(d)));
      this.thumbItems.forEach((t) => setHidden(t, !show(t)));
      const lightbox = qs(this.parentElement, 'fsb-pdp-lightbox');
      if (lightbox && lightbox.applyFilter) lightbox.applyFilter(ids);
      this.relabel();
      const target = this.active && !this.active.hidden ? this.active : this.visible()[0];
      this.active = null;
      this.goTo(target, { behavior: 'auto' }); // re-align the track after slides appeared or disappeared
    }

    relabel() {
      const s = stringsFor(this);
      const vis = this.visible();
      const count = vis.length;
      vis.forEach((slide, i) => {
        if (s.slideLabel) setAttr(slide, 'aria-label', fill(s.slideLabel, { index: i + 1, count }));
        if (s.zoomLabel) setText(qs(slide, '[data-fsb-zoom-label]'), fill(s.zoomLabel, { index: i + 1, count }));
      });
      this.thumbItems.forEach((item) => {
        const i = vis.findIndex((slide) => mediaId(slide) === mediaId(item));
        if (i >= 0 && s.thumbLabel) setAttr(qs(item, '[data-fsb-thumb]'), 'aria-label', fill(s.thumbLabel, { index: i + 1, count }));
      });
      [this.prev, this.next, this.counter, this.dotsWrap, this.thumbsWrap].forEach((el) => setHidden(el, count < 2));
      setAttr(this, 'data-fsb-visible-count', String(count));
    }

    // Desktop hover zoom (opt-in): scale the image under the mouse, origin following the pointer.
    setupHoverZoom() {
      if (!this.hasAttribute('data-fsb-hover-zoom') || !window.matchMedia) return;
      this.zoomQuery = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
      this.on(this.track, 'pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.zoomEvent = e;
        if (!this.zoomFrame) this.zoomFrame = requestAnimationFrame(() => this.hoverZoom());
      }, { passive: true });
      this.on(this.track, 'pointerleave', () => this.resetZoom());
      this.motionFn = () => this.resetZoom();
      motionWatchers.add(this.motionFn);
    }

    hoverZoom() {
      this.zoomFrame = 0;
      const e = this.zoomEvent;
      const slide = e && e.target && e.target.closest ? e.target.closest('[data-fsb-media-type="image"]') : null;
      const img = slide && this.track.contains(slide) ? qs(slide, 'img') : null;
      if (!img || reduceMotion || !this.zoomQuery.matches) return this.resetZoom();
      if (this.zoomImg !== img) {
        this.resetZoom();
        this.zoomImg = img;
        this.zoomSlide = slide;
        useLargest(img);
        setAttr(slide, 'data-fsb-zooming', '');
      }
      zoomTo(img, originAt(img, e.clientX, e.clientY), 2);
    }

    resetZoom() {
      if (!this.zoomImg) return;
      zoomTo(this.zoomImg, null, 0);
      setAttr(this.zoomSlide, 'data-fsb-zooming', null);
      this.zoomImg = this.zoomSlide = null;
    }
  }

  /* ---------------------------------------------------------------- lightbox */

  class FsbPdpLightbox extends Base {
    setup() {
      const dialog = (this.dialog = qs(this, '[data-fsb-lightbox]'));
      if (!dialog) return;
      this.track = qs(dialog, '[data-fsb-lightbox-track]');
      this.slides = kids(this.track, 'data-fsb-media-id');
      this.counter = qs(dialog, '[data-fsb-lightbox-counter]');
      this.prev = qs(dialog, '[data-fsb-lightbox-prev]');
      this.next = qs(dialog, '[data-fsb-lightbox-next]');
      this.closeButton = qs(dialog, '[data-fsb-lightbox-close]');
      this.on(doc, 'click', (e) => {
        const trigger = closestIn(e, '[data-fsb-lightbox-open]');
        if (!trigger || trigger.getAttribute('aria-controls') !== dialog.id) return;
        e.preventDefault();
        this.open(mediaId(trigger), trigger);
      });
      this.on(this.closeButton, 'click', () => this.close());
      this.on(this.prev, 'click', () => this.step(-1));
      this.on(this.next, 'click', () => this.step(1));
      this.on(dialog, 'close', () => this.afterClose()); // Escape and other native closes
      this.on(dialog, 'click', (e) => e.target === dialog && this.close());
      this.on(dialog, 'keydown', (e) => this.onKey(e));
      if (!this.track) return;
      const passive = { passive: true };
      this.on(this.track, 'scroll', () => this.onScroll(), passive);
      this.on(this.track, 'click', (e) => this.onImageClick(e));
      this.on(this.track, 'pointerdown', (e) => this.onPointerDown(e), passive);
      this.on(this.track, 'pointermove', (e) => this.onPointerMove(e), passive);
      this.on(this.track, 'pointerup', () => (this.drag = null), passive);
      this.on(this.track, 'pointercancel', () => (this.drag = null), passive);
    }

    teardown() {
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.panFrame);
      this.scrollFrame = this.panFrame = 0;
      if (this.locked) lock(false);
      this.locked = false;
    }

    visible() {
      return (this.slides || []).filter((s) => !s.hidden);
    }

    // Images carry only data-src/data-srcset until the first open, so nothing downloads on page load.
    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      qsa(this.track, 'img[data-src]').forEach((img) => {
        if (img.getAttribute('data-srcset')) img.srcset = img.getAttribute('data-srcset');
        if (!img.getAttribute('src')) img.src = img.getAttribute('data-src');
      });
    }

    open(id, trigger) {
      const dialog = this.dialog;
      if (dialog.open) return;
      this.trigger = trigger || doc.activeElement;
      this.hydrate();
      if (!this.locked) lock(true);
      this.locked = true;
      openDialog(dialog);
      const vis = this.visible();
      this.show(vis.find((s) => mediaId(s) === String(id)) || vis[0], 'auto');
      focusEl(this.closeButton);
    }

    // Our own close paths clean up synchronously; the native close event (fired on the next frame in Chrome) is
    // then a no-op, and a stale one never unlocks a dialog that was opened again in the meantime.
    close() {
      closeDialog(this.dialog);
      this.afterClose();
    }

    afterClose() {
      if (!this.locked || this.dialog.open) return;
      this.resetZoom();
      lock(false);
      this.locked = false;
      // Show the last viewed image in the gallery, and return focus to its zoom button (the equivalent trigger).
      const gallery = qs(this.parentElement, 'fsb-pdp-gallery');
      if (this.active && gallery && gallery.goToMedia) gallery.goToMedia(mediaId(this.active), { behavior: 'auto' });
      let target = this.trigger;
      if (target && gallery && gallery.active && target.closest('[data-fsb-gallery-track]')) target = qs(gallery.active, '[data-fsb-lightbox-open]') || target;
      if (target && target.isConnected && !target.closest('[hidden]')) focusEl(target);
      else focusEl(qs(gallery, '[data-fsb-gallery-track]'));
      this.trigger = null;
    }

    onKey(e) {
      const vis = this.visible();
      if (e.altKey || e.ctrlKey || e.metaKey || vis.length < 2) return;
      if (e.key === 'ArrowRight') this.step(1);
      else if (e.key === 'ArrowLeft') this.step(-1);
      else if (e.key === 'Home') this.show(vis[0]);
      else if (e.key === 'End') this.show(vis[vis.length - 1]);
      else return;
      e.preventDefault();
    }

    step(dir) {
      const vis = this.visible();
      if (vis.length < 2) return;
      const i = Math.max(0, vis.indexOf(this.active));
      this.show(vis[(i + dir + vis.length) % vis.length]);
    }

    show(slide, behavior) {
      if (!slide || !this.track) return;
      this.setActive(slide);
      this.pending = slide;
      this.pendingUntil = performance.now() + 900;
      scrollToSlide(this.track, slide, behavior || behaviorFor(this.dialog));
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const slide = this.dialog.open ? nearest(this.track, this.visible()) : null;
        if (!slide || (this.pending && slide !== this.pending && performance.now() < this.pendingUntil)) return;
        this.pending = null;
        this.setActive(slide);
      });
    }

    setActive(slide) {
      if (!slide) return;
      const s = stringsFor(this);
      const vis = this.visible();
      if (this.active !== slide) this.resetZoom();
      this.active = slide;
      this.slides.forEach((item) => setAttr(item, 'data-fsb-active', item === slide ? '' : null));
      if (s.lightboxCounter) setText(this.counter, fill(s.lightboxCounter, { index: vis.indexOf(slide) + 1, count: vis.length }));
    }

    applyFilter(ids) {
      (this.slides || []).forEach((s) => setHidden(s, Boolean(ids) && !ids.has(String(mediaId(s)))));
      const vis = this.visible();
      setHidden(this.prev, vis.length < 2);
      setHidden(this.next, vis.length < 2);
      this.setActive(this.active && !this.active.hidden ? this.active : vis[0]);
    }

    // Click or tap toggles a 2x zoom at the pointer; the mouse pans by moving, touch pans by dragging.
    onImageClick(e) {
      const img = closestIn(e, 'img');
      if (!img || !this.track.contains(img)) return;
      if (this.moved) return (this.moved = false);
      const same = this.zoomImg === img;
      this.resetZoom();
      if (same) return;
      useLargest(img);
      this.zoomImg = img;
      this.zoomSlide = img.closest('[data-fsb-media-id]');
      this.origin = originAt(img, e.clientX, e.clientY);
      setAttr(this.zoomSlide, 'data-fsb-zoomed', '');
      img.style.touchAction = 'none';
      zoomTo(img, this.origin, 2);
    }

    onPointerDown(e) {
      if (!this.zoomImg || e.target !== this.zoomImg || e.pointerType === 'mouse') return;
      this.drag = { x: e.clientX, y: e.clientY, origin: this.origin };
      this.moved = false;
    }

    onPointerMove(e) {
      if (!this.zoomImg || (e.pointerType !== 'mouse' && !this.drag)) return;
      this.panEvent = e;
      this.panFrame = this.panFrame || requestAnimationFrame(() => {
        const img = this.zoomImg;
        const p = this.panEvent;
        this.panFrame = 0;
        if (!img) return;
        if (p.pointerType === 'mouse') {
          if (p.target !== img) return;
          this.origin = originAt(img, p.clientX, p.clientY);
        } else if (this.drag) {
          // At 2x, moving the origin by -dx/width keeps the image under the finger.
          const b = boxOf(img);
          const dx = p.clientX - this.drag.x;
          const dy = p.clientY - this.drag.y;
          if (Math.abs(dx) + Math.abs(dy) > 6) this.moved = true;
          this.origin = { x: clamp(this.drag.origin.x - (dx / (b.w || 1)) * 100, 0, 100), y: clamp(this.drag.origin.y - (dy / (b.h || 1)) * 100, 0, 100) };
        }
        zoomTo(img, this.origin);
      });
    }

    resetZoom() {
      if (!this.zoomImg) return;
      zoomTo(this.zoomImg, null, 0);
      this.zoomImg.style.touchAction = '';
      setAttr(this.zoomSlide, 'data-fsb-zoomed', null);
      this.zoomImg = this.zoomSlide = this.drag = null;
    }
  }

  /* ---------------------------------------------------------------- size guide dialog */

  class FsbPdpModal extends Base {
    setup() {
      const dialog = (this.dialog = qs(this, '[data-fsb-modal]'));
      if (!dialog) return;
      this.on(doc, 'click', (e) => {
        const trigger = closestIn(e, '[data-fsb-size-guide-open]');
        if (!trigger || trigger.getAttribute('data-fsb-size-guide-open') !== dialog.id) return;
        e.preventDefault();
        this.open(trigger);
      });
      this.on(dialog, 'click', (e) => {
        const unit = closestIn(e, '[data-fsb-unit-toggle]');
        if (unit) this.setUnit(unit.getAttribute('data-fsb-unit-toggle'));
        else if (closestIn(e, '[data-fsb-modal-close]') || isBackdrop(dialog, e)) this.close();
      });
      this.on(dialog, 'close', () => this.afterClose()); // Escape and other native closes
    }

    teardown() {
      if (this.locked) lock(false);
      this.locked = false;
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.trigger = trigger || doc.activeElement;
      if (!this.locked) lock(true);
      this.locked = true;
      openDialog(this.dialog);
      focusEl(qs(this.dialog, '[data-fsb-modal-close]'));
    }

    close() {
      closeDialog(this.dialog);
      this.afterClose();
    }

    // Idempotent: runs once per open, synchronously for our own close paths (see FsbPdpLightbox#close).
    afterClose() {
      if (!this.locked || this.dialog.open) return;
      lock(false);
      this.locked = false;
      if (this.trigger && this.trigger.isConnected) focusEl(this.trigger);
      this.trigger = null;
    }

    setUnit(unit) {
      qsa(this.dialog, '[data-fsb-unit-toggle]').forEach((b) => setAttr(b, 'aria-pressed', String(b.getAttribute('data-fsb-unit-toggle') === unit)));
      qsa(this.dialog, '[data-fsb-unit-panel]').forEach((p) => setHidden(p, p.getAttribute('data-fsb-unit-panel') !== unit));
    }
  }

  /* ---------------------------------------------------------------- accordion */

  class FsbPdpAccordion extends Base {
    setup() {
      this.trigger = qs(this, '[data-fsb-acc-trigger]');
      this.panel = qs(this, '[data-fsb-acc-panel]');
      if (!this.trigger || !this.panel) return;
      this.on(this.trigger, 'click', () => this.setOpen(this.trigger.getAttribute('aria-expanded') !== 'true', true));
      // Find-in-page reveals hidden="until-found" content; the browser removes [hidden] itself.
      this.on(this.panel, 'beforematch', () => setAttr(this.trigger, 'aria-expanded', 'true'));
      this.on(doc, 'shopify:block:select', (e) => (e.target === this || this.contains(e.target)) && this.setOpen(true, false));
    }

    teardown() {
      this.stopAnimation();
    }

    open() {
      this.setOpen(true, false);
    }

    stopAnimation() {
      const anim = this.anim;
      this.anim = null;
      if (anim) anim.cancel();
      if (this.panel) this.panel.style.overflow = '';
    }

    // Height animation is the one allowed non-transform animation; it is skipped under reduced motion.
    setOpen(open, animate) {
      const panel = this.panel;
      if (!panel) return;
      this.stopAnimation();
      setAttr(this.trigger, 'aria-expanded', String(open));
      const closed = panel.hasAttribute('hidden');
      if (open === !closed) return;
      const animated = animate && motionOk(this) && typeof panel.animate === 'function';
      if (open) panel.removeAttribute('hidden');
      if (!animated) return open || panel.setAttribute('hidden', 'until-found');
      const heights = open ? [0, panel.scrollHeight] : [panel.offsetHeight, 0];
      const anim = (this.anim = panel.animate(heights.map((h) => ({ height: `${h}px` })), { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' }));
      panel.style.overflow = 'hidden';
      anim.onfinish = () => {
        if (this.anim !== anim) return;
        this.anim = null;
        panel.style.overflow = '';
        if (!open) panel.setAttribute('hidden', 'until-found');
      };
    }
  }

  /* ---------------------------------------------------------------- quantity stepper */

  class FsbPdpQuantity extends Base {
    setup() {
      this.input = qs(this, '[data-fsb-quantity-input]');
      this.minus = qs(this, '[data-fsb-qty-minus]');
      this.plus = qs(this, '[data-fsb-qty-plus]');
      if (!this.input) return;
      this.on(this.minus, 'click', () => this.step(-1));
      this.on(this.plus, 'click', () => this.step(1));
      this.on(this.input, 'change', () => this.write(this.valid(parseInt(this.input.value, 10)), false));
      this.paint();
    }

    rules() {
      const read = (name) => parseInt(this.input.getAttribute(name), 10);
      const min = Math.max(1, read('min') || 1);
      const max = read('max');
      return { min, step: Math.max(1, read('step') || 1), max: Number.isFinite(max) ? Math.max(min, max) : null };
    }

    valid(value) {
      const { min, step, max } = this.rules();
      let n = Number.isFinite(value) ? Math.round(value / step) * step : min;
      if (n < min) n = min;
      if (max !== null && n > max) n = Math.max(min, Math.floor(max / step) * step);
      return n;
    }

    step(dir) {
      const { min, step } = this.rules();
      const current = parseInt(this.input.value, 10);
      const base = Number.isFinite(current) ? current : min;
      this.write(this.valid(dir > 0 && base < min ? min : base + dir * step), true);
    }

    write(value, notify) {
      const changed = this.input.value !== String(value);
      if (changed) this.input.value = String(value);
      this.paint();
      if (changed && notify) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Called by fsb-pdp-product when the variant or the cart quantity changes.
    setRules(rules) {
      if (!this.input) return;
      this.input.min = String(rules.min);
      this.input.step = String(rules.step);
      if (rules.max == null) this.input.removeAttribute('max');
      else this.input.max = String(rules.max);
      if (rules.value != null) this.input.value = String(rules.value);
      this.write(this.valid(parseInt(this.input.value, 10)), false);
    }

    // aria-disabled (not disabled) keeps keyboard focus on a button that reaches its limit.
    paint() {
      const { min, step, max } = this.rules();
      const value = parseInt(this.input.value, 10) || min;
      setAttr(this.minus, 'aria-disabled', value <= min ? 'true' : null);
      setAttr(this.plus, 'aria-disabled', max !== null && value + step > max ? 'true' : null);
    }
  }

  /* ---------------------------------------------------------------- share */

  class FsbPdpShare extends Base {
    setup() {
      this.status = qs(this, '[data-fsb-share-status]');
      this.on(qs(this, '[data-fsb-share-button]'), 'click', () => this.share());
    }

    // Web Share, then the clipboard ("Link copied"), then a visible field with the link selected.
    async share() {
      const url = this.getAttribute('data-share-url') || location.href;
      const data = { title: this.getAttribute('data-share-title') || doc.title, url };
      if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
        try {
          return await navigator.share(data);
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        }
      }
      const copied = stringsFor(this).linkCopied;
      if (copied && navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(url);
          setText(this.status, copied);
          this.stop(this.statusTimer);
          this.statusTimer = this.wait(() => setText(this.status, ''), 4000);
          return;
        } catch (e) {
          /* fall back to the copy field */
        }
      }
      const input = qs(this, '[data-fsb-share-input]');
      setHidden(qs(this, '[data-fsb-share-fallback]'), false);
      if (!input) return;
      input.focus();
      input.select();
    }
  }

  /* ---------------------------------------------------------------- product root */

  class FsbPdpProduct extends Base {
    setup() {
      const p = (this.product = productDataFor(this));
      this.sectionId = this.getAttribute('data-section-id') || '';
      this.strings = stringsFor(this);
      if (!p || !Array.isArray(p.variants) || !p.variants.length) return;
      this.variants = p.variants;
      this.optionNames = Array.isArray(p.options) ? p.options : [];
      this.fallback = this.variants.find((v) => v.available) || this.variants[0];
      this.fieldsets = qsa(this, '[data-fsb-option-fieldset]');
      this.atc = qs(this, '[data-fsb-atc]');
      this.atcLabel = qs(this.atc, '[data-fsb-atc-label]');
      this.form = this.atc && this.atc.form;
      this.formError = qs(this, '[data-fsb-form-error]');
      this.statusText = qs(this, '[data-fsb-form-status-text]');
      this.bagLink = qs(this, '[data-fsb-bag-link]');
      this.gallery = qs(this, 'fsb-pdp-gallery');
      this.sizeIndex = this.findSizeIndex();
      this.atcState = this.atc ? this.atc.getAttribute('data-state') || 'idle' : 'idle';
      this.serverKey = p.selectedVariantId == null ? 'product' : String(p.selectedVariantId);
      this.rendered = { price: this.serverKey, qty: '', rules: null, size: undefined };
      this.busy = this.uncertain = false;
      this.on(this, 'change', (e) => e.target.matches && e.target.matches('[data-fsb-option-input]') && this.onOptionChange());
      this.on(this, 'click', (e) => this.onScrollLink(e));
      this.on(this.form, 'submit', (e) => this.onSubmit(e));
      this.on(doc, 'fsb:pdp:request-add', (e) => e.detail && String(e.detail.sectionId) === this.sectionId && this.requestAdd(e.detail.trigger));
      this.on(window, 'pageshow', (e) => this.onPageShow(e));
      if (!designMode()) qsa(this, 'a[data-fsb-scroll-to]').forEach((a) => this.scrollTarget(a) || a.removeAttribute('href'));
      this.setupStickyCheck();
      this.apply(this.readSelection(), 'init');
    }

    teardown() {
      if (this.boxObserver) this.boxObserver.disconnect();
      cancelAnimationFrame(this.boxFrame);
      this.boxFrame = 0;
    }

    /* ---------- selection ---------- */

    // Checked radios win (they may have been restored by the browser); options without a picker use the server variant.
    readSelection() {
      const base = this.variants.find((v) => String(v.id) === String(this.product.selectedVariantId)) || this.fallback;
      const sel = this.optionNames.map((n, i) => (base.options && base.options[i] != null ? base.options[i] : null));
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.getAttribute('data-fsb-option-index'));
        const checked = qs(fs, '[data-fsb-option-input]:checked');
        if (i >= 0 && i < sel.length) sel[i] = checked ? checked.value : null;
      });
      return sel;
    }

    findVariant(sel) {
      if (sel.some((v) => v === null)) return null;
      return this.variants.find((v) => Array.isArray(v.options) && sel.every((value, i) => v.options[i] === value)) || null;
    }

    findSizeIndex() {
      const fs = this.fieldsets.find((f) => qs(f, '[data-fsb-size-guide-open]'));
      return fs ? Number(fs.getAttribute('data-fsb-option-index')) : this.optionNames.findIndex((n) => norm(n) === 'size');
    }

    // One option value combined with the other options' current selections (unselected options are ignored).
    valueState(index, value, sel) {
      let exists = false;
      for (const v of this.variants) {
        const o = v.options || [];
        if (o[index] !== value || sel.some((s, j) => j !== index && s !== null && o[j] !== s)) continue;
        if (v.available) return 'available';
        exists = true;
      }
      return exists ? 'soldout' : 'unavailable';
    }

    selectedList(sel) {
      return sel.map((value, i) => (value === null ? null : { name: this.optionNames[i] || '', value })).filter(Boolean);
    }

    onOptionChange() {
      const sel = this.readSelection();
      const pending = this.selectEvent(sel);
      this.apply(sel, 'user');
      if (!pending) return;
      const v = this.state.variant;
      try {
        pending.resolve(
          v
            ? { variant: { id: v.id, title: v.title, availableForSale: Boolean(v.available), price: { amount: v.price_amount, currencyCode: this.product.currencyCode }, selectedOptions: this.selectedList(sel) } }
            : { variant: null }
        );
      } catch (e) {
        /* analytics only */
      }
    }

    // source: init (first render, server already matches) | user (option change) | resync (pageshow / restored form state)
    apply(sel, source) {
      const variant = this.findVariant(sel);
      const complete = sel.every((v) => v !== null);
      this.state = { selection: sel, variant, complete };
      this.renderOptions(sel);
      this.renderVariant(variant, source !== 'init');
      if (source !== 'init' || (variant ? String(variant.id) : 'product') !== this.serverKey) {
        this.syncGallery(variant, sel, complete, source);
        this.updateUrl(variant);
      }
      if (source === 'user') {
        this.clearMessages();
        this.stop(this.announceTimer);
        this.announceTimer = this.wait(() => this.announce(), 250);
      }
      this.emit('fsb:pdp:variant-change', this.snapshot());
      // Dawn's gift card recipient form listens for variant-change with a non-null variant.
      if (variant && this.form && qs(this.form, 'recipient-form')) publishSafe('variantChange', { data: { sectionId: this.sectionId, html: '', variant } });
    }

    // Also read by fsb-pdp-sticky-bar, which connects after the initial variant-change event.
    snapshot() {
      const st = this.state || { selection: [], variant: null, complete: false };
      return {
        sectionId: this.sectionId,
        productId: this.product ? this.product.id : Number(this.getAttribute('data-product-id')),
        variant: st.variant || null,
        selectedOptions: st.selection.slice(),
        complete: Boolean(st.complete),
        exists: Boolean(st.variant),
        atcState: this.atcState,
        locked: Boolean(this.uncertain),
        canAdd: Boolean(this.form),
      };
    }

    /* ---------- rendering ---------- */

    renderOptions(sel) {
      const s = this.strings;
      this.fieldsets.forEach((fs) => {
        const index = Number(fs.getAttribute('data-fsb-option-index'));
        const current = sel[index] == null ? null : sel[index];
        setText(qs(fs, '[data-fsb-option-selected]'), current === null ? '' : current);
        if (current !== null && fs.hasAttribute('aria-invalid')) {
          fs.removeAttribute('aria-invalid');
          setText(qs(fs, '[data-fsb-option-error]'), '');
        }
        qsa(fs, '[data-fsb-option-value]').forEach((wrap) => {
          const input = qs(wrap, '[data-fsb-option-input]');
          if (!input) return;
          const state = this.valueState(index, input.value, sel);
          const checked = input.value === current;
          if (input.checked !== checked) input.checked = checked;
          setAttr(wrap, 'data-fsb-value-state', state);
          setHidden(qs(wrap, '[data-fsb-option-note]'), state !== 'soldout');
          setText(qs(wrap, '[data-fsb-option-status]'), (state === 'soldout' ? s.optionSoldOut : state === 'unavailable' ? s.optionUnavailable : '') || '');
        });
      });
    }

    renderVariant(v, reset) {
      const key = v ? String(v.id) : 'product';
      if (this.rendered.price !== key) {
        this.rendered.price = key;
        qsa(this, '[data-fsb-price]').forEach((el) => (el.innerHTML = (v ? v.price_html : this.product.priceHtml) || ''));
        qsa(this, '[data-fsb-inventory]').forEach((el) => (el.innerHTML = (v && v.inventory_html) || ''));
      }
      const sku = v && v.sku ? String(v.sku) : '';
      qsa(this, '[data-fsb-sku-wrapper]').forEach((el) => setHidden(el, !sku));
      qsa(this, '[data-fsb-sku]').forEach((el) => setText(el, sku));
      const ref = v || this.fallback;
      qsa(this, '[data-fsb-trust-exchange]').forEach((el) => setHidden(el, ref.exchange_eligible === false));
      const idInput = qs(this, '[data-fsb-variant-id]');
      if (idInput && idInput.value !== (v ? String(v.id) : '')) idInput.value = v ? String(v.id) : '';
      this.renderQuantity(reset);
      this.renderSizeRows();
      this.applyBaseState();
    }

    // Quantity rules follow Dawn (product-info.js): once the cart holds the minimum, single increments are allowed.
    renderQuantity(reset) {
      const v = (this.state && this.state.variant) || this.fallback;
      const s = this.strings;
      const rule = v.quantity_rule || {};
      const min = Math.max(1, Number(rule.min) || 1);
      const step = Math.max(1, Number(rule.increment) || 1);
      const max = rule.max == null ? null : Number(rule.max);
      const inCart = Number(v.cart_quantity) || 0;
      const left = max === null ? null : Math.max(0, max - inCart);
      let lowest = left === null ? min : Math.min(min, left);
      if (inCart >= min) lowest = Math.min(lowest, step);
      lowest = Math.max(1, lowest);
      this.limitReached = left === 0;
      const key = [v.id, min, step, max, inCart].join(':');
      if (key === this.rendered.qty && !reset) return;
      const first = !this.rendered.qty;
      this.rendered.qty = key;
      qsa(this, '[data-fsb-quantity-input]').forEach((input) => {
        if (input.type === 'hidden') return !first && (input.value = String(lowest));
        input.setAttribute('data-min', String(min));
        input.setAttribute('data-cart-quantity', String(inCart));
        setAttr(input, 'data-max', max === null ? null : String(max));
      });
      const stepper = qs(this, 'fsb-pdp-quantity');
      if (stepper && stepper.setRules) stepper.setRules({ min: lowest, max: left === null ? null : Math.max(lowest, left), step, value: reset ? lowest : null });
      const rulesKey = [min, step, max].join(':');
      const rulesEl = qs(this, '[data-fsb-quantity-rules]');
      if (rulesEl && this.rendered.rules !== null && this.rendered.rules !== rulesKey) {
        rulesEl.textContent = '';
        [step > 1 && fill(s.multiplesOf, { quantity: step }), min > 1 && fill(s.minimumOf, { quantity: min }), max !== null && fill(s.maximumOf, { quantity: max })]
          .filter(Boolean)
          .forEach((text) => rulesEl.appendChild(doc.createElement('span')).append(text));
      }
      this.rendered.rules = rulesKey;
      const note = qs(this, '[data-fsb-quantity-cart]');
      if (inCart > 0 && s.inCart) setText(note, fill(s.inCart, { quantity: inCart }));
      setHidden(note, inCart < 1);
    }

    // Marks the first chart row with a cell equal to the selected size (also compared without inch marks);
    // a centimetre table mirrors the row index of its inch table.
    renderSizeRows() {
      const value = this.sizeIndex < 0 ? null : this.state.selection[this.sizeIndex];
      const size = value == null ? '' : String(value).trim();
      if (size === this.rendered.size) return;
      this.rendered.size = size;
      const plain = size.replace(/"/g, '').trim();
      const matches = (row) => size && qsa(row, 'th, td').some((c) => c.textContent.trim() === size || c.textContent.replace(/"/g, '').trim() === plain);
      const mark = (table, active) =>
        qsa(table, '[data-fsb-size-row]').forEach((row, i) => {
          setAttr(row, 'data-fsb-size-row-current', i === active ? '' : null);
          setAttr(row, 'aria-current', i === active ? 'true' : null);
        });
      const unit = (t) => (t.closest('[data-fsb-unit-panel]') ? t.closest('[data-fsb-unit-panel]').getAttribute('data-fsb-unit-panel') : '');
      const tables = qsa(this, '[data-fsb-size-table]');
      let mainIndex = -1;
      tables.filter((t) => unit(t) !== 'cm').forEach((t) => {
        const index = qsa(t, '[data-fsb-size-row]').findIndex(matches);
        if (unit(t)) mainIndex = index;
        mark(t, index);
      });
      tables.filter((t) => unit(t) === 'cm').forEach((t) => mark(t, mainIndex));
    }

    computeBaseState() {
      const { variant: v, complete } = this.state;
      if (v ? v.requires_selling_plan : this.variants.every((x) => x.requires_selling_plan)) return 'unavailable';
      if (!complete) return 'idle'; // incomplete selection keeps the CTA enabled; submit explains what is missing
      if (!v) return 'unavailable';
      if (!v.available) return 'soldout';
      return this.limitReached ? 'unavailable' : 'idle';
    }

    applyBaseState() {
      const s = this.strings;
      const v = this.state.variant;
      this.baseState = this.computeBaseState();
      const ready = this.baseState === 'idle' && Boolean(v);
      const dynamic = qs(this, '[data-fsb-dynamic-checkout]');
      if (dynamic && dynamic.hidden === ready) {
        dynamic.hidden = !ready;
        const pay = window.Shopify && window.Shopify.PaymentButton;
        if (ready && pay && typeof pay.init === 'function') {
          try {
            pay.init();
          } catch (e) {
            /* Shopify renders the buttons on its own when it can */
          }
        }
      }
      qsa(this, '[data-fsb-notify]').forEach((a) => setHidden(a, this.baseState !== 'soldout'));
      // Cart already holds the maximum allowed by the quantity rule: explain why the button is unavailable.
      const limit = this.limitReached && v && v.available && !this.uncertain ? s.limitReached || '' : '';
      if (limit) setText(this.statusText, limit);
      else if (this.limitShown && this.statusText && this.statusText.textContent === s.limitReached) setText(this.statusText, '');
      this.limitShown = Boolean(limit);
      if (!this.busy && !this.transient) this.setAtc(this.baseState);
    }

    setAtc(state) {
      this.atcState = state;
      paintButton(this.atc, this.atcLabel, state, this.uncertain, this.strings);
      this.emit('fsb:pdp:atc-state', { sectionId: this.sectionId, state, locked: Boolean(this.uncertain) });
    }

    // Temporary success / error state, then back to the state of the current selection.
    flash(state) {
      this.stop(this.transient);
      this.setAtc(state);
      this.transient = this.wait(() => {
        this.transient = 0;
        this.setAtc(this.baseState);
      }, 2500);
    }

    clearMessages() {
      setText(this.formError, '');
      if (this.uncertain) return;
      setText(this.statusText, '');
      setHidden(this.bagLink, true);
      this.limitShown = false;
    }

    /* ---------- gallery, URL, live region, analytics ---------- */

    // Returns a Set of visible media ids, null (show all) or undefined (leave the gallery as it is).
    visibleMedia(v, sel, complete) {
      const mode = this.getAttribute('data-media-filter');
      if (!this.gallery || !this.gallery.getMedia || (mode !== 'variant_media' && mode !== 'alt_text')) return undefined;
      if (!v) return complete ? undefined : null;
      const media = this.gallery.getMedia();
      let ids;
      if (mode === 'variant_media') {
        // Hide media featured by other variants; keep shared media and this variant's own media.
        const map = this.product.mediaVariantMap || {};
        ids = media.filter((m) => !map[m.id] || m.id === String(v.featured_media_id)).map((m) => m.id);
      } else {
        // Hide media whose alt text equals another value of an option (in practice: another colour).
        const sets = this.optionNames.map((n, i) => new Set(this.variants.map((x) => norm((x.options || [])[i]))));
        ids = media.filter((m) => !norm(m.alt) || !sets.some((set, i) => set.has(norm(m.alt)) && norm(m.alt) !== norm(sel[i]))).map((m) => m.id);
      }
      return ids.length ? new Set(ids) : null;
    }

    syncGallery(v, sel, complete, source) {
      const gallery = this.gallery;
      if (!gallery || !gallery.applyFilter) return;
      const ids = this.visibleMedia(v, sel, complete);
      if (ids !== undefined) gallery.applyFilter(ids);
      if (v && v.featured_media_id != null) gallery.goToMedia(v.featured_media_id, { behavior: source === 'user' ? behaviorFor(this) : 'auto' });
    }

    updateUrl(v) {
      const handle = this.getAttribute('data-product-handle') || this.product.handle;
      if (this.getAttribute('data-update-url') !== 'true' || designMode() || !handle) return;
      try {
        if (!decodeURIComponent(location.pathname).endsWith(`/products/${handle}`)) return; // only the page's own product
        const url = new URL(location.href);
        if (v) url.searchParams.set('variant', String(v.id));
        else url.searchParams.delete('variant');
        if (url.href !== location.href) history.replaceState(history.state, '', url.href);
      } catch (e) {
        /* URL sync is a convenience only */
      }
    }

    // "{options} selected. {price}. {status}" (debounced 250 ms by apply()).
    announce() {
      const s = this.strings;
      const { variant: v, complete, selection } = this.state;
      const live = qs(this, '[data-fsb-live]');
      const price = qs(this, '[data-fsb-price]');
      const status = v ? (v.available ? s.inStock : s.optionSoldOut) : complete ? s.optionUnavailable : '';
      const text = tidy(fill(s.variantAnnouncement, { options: selection.filter((x) => x !== null).join(', '), price: price ? price.textContent : '', status: status || '' }));
      if (!live || !text) return;
      live.textContent = '';
      requestAnimationFrame(() => (live.textContent = text));
    }

    selectEvent(sel) {
      const SelectEvent = standard('ProductSelectEvent');
      if (!SelectEvent) return null;
      try {
        const deferred = SelectEvent.createPromise();
        const p = this.product;
        (qs(this, '[data-fsb-options]') || this).dispatchEvent(
          new SelectEvent({ product: { id: String(p.id), title: p.title, handle: p.handle }, selectedOptions: this.selectedList(sel), promise: deferred.promise })
        );
        return deferred;
      } catch (e) {
        return null;
      }
    }

    /* ---------- links, sticky buy box, page lifecycle ---------- */

    scrollTarget(link) {
      const ref = link.getAttribute('data-fsb-scroll-to') || '';
      try {
        return ref.charAt(0) === '#' ? doc.getElementById(decodeURIComponent(ref.slice(1))) : null;
      } catch (e) {
        return null;
      }
    }

    // Read more / rating links: open the target accordion, scroll to it and move focus (trigger, or the reviews section).
    onScrollLink(e) {
      const link = closestIn(e, '[data-fsb-scroll-to]');
      if (!link || !this.contains(link)) return;
      e.preventDefault();
      const target = this.scrollTarget(link);
      if (!target) return; // reviews section not on the page
      const acc = target.closest('fsb-pdp-accordion');
      if (acc && acc.open) acc.open();
      else if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) target.setAttribute('tabindex', '-1');
      (acc || target).scrollIntoView({ behavior: behaviorFor(this), block: 'start' });
      focusEl(acc ? qs(acc, '[data-fsb-acc-trigger]') : target);
    }

    // Desktop: the buy box stays sticky only while it fits (viewport - header - 48px); else fsb-pdp-buy--static.
    setupStickyCheck() {
      const box = qs(this, '[data-fsb-buy-box]');
      if (!box || box.getAttribute('data-fsb-sticky') !== 'true' || !window.matchMedia) return;
      const schedule = () => {
        this.boxFrame = this.boxFrame || requestAnimationFrame(() => {
          this.boxFrame = 0;
          if (!desktop.matches) return;
          const header = parseFloat(getComputedStyle(html).getPropertyValue('--fsb-header-height')) || 112;
          box.classList.toggle('fsb-pdp-buy--static', box.offsetHeight > innerHeight - header - 48);
        });
      };
      const desktop = watchMedia('(min-width: 990px)', schedule, this.ac.signal);
      if ('ResizeObserver' in window) (this.boxObserver = new ResizeObserver(schedule)).observe(box);
      this.on(window, 'resize', schedule, { passive: true });
      schedule();
    }

    onPageShow(e) {
      const sel = this.readSelection();
      if (sel.some((v, i) => v !== this.state.selection[i])) this.apply(sel, 'resync');
      if (!e.persisted) return;
      // Back from the bag (bfcache): unlock an uncertain add and refresh cart counts and the header bubble.
      this.uncertain = this.busy = false;
      this.stop(this.transient);
      this.transient = 0;
      if (this.form) this.form.removeAttribute('aria-busy');
      this.clearMessages();
      this.applyBaseState();
      this.syncCart(null, true);
    }

    /* ---------- add to cart (BUILD-SPEC §8, docs/understand-dawn-integration.md) ---------- */

    showMissing() {
      let first = null;
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.getAttribute('data-fsb-option-index'));
        if (this.state.selection[i] !== null) return;
        const name = fs.getAttribute('data-fsb-option-fieldset') || this.optionNames[i] || '';
        setText(qs(fs, '[data-fsb-option-error]'), fill(this.strings.chooseOption, { option: name.toLocaleLowerCase() }));
        fs.setAttribute('aria-invalid', 'true');
        first = first || fs;
      });
      if (!first) return;
      first.scrollIntoView({ behavior: behaviorFor(this), block: 'center' });
      focusEl(qs(first, '[data-fsb-option-input]'));
    }

    // fsb:pdp:request-add from the sticky bar: run the normal submit path (validation, fetch, states).
    // `trigger` (the sticky button) is where the cart notification returns focus; a set origin marks a sticky add.
    requestAdd(trigger) {
      if (!this.form || this.busy || this.uncertain || this.atc.disabled) return;
      this.origin = trigger && trigger.isConnected ? trigger : this.atc;
      if (this.form.requestSubmit) this.form.requestSubmit(this.atc);
      else this.atc.click();
      this.origin = null;
    }

    onSubmit(e) {
      const origin = this.origin || null;
      if (this.busy || this.uncertain) return e.preventDefault();
      if (!this.state.complete) {
        e.preventDefault();
        return this.showMissing();
      }
      if (this.baseState !== 'idle' || !this.state.variant) return e.preventDefault();
      const cart = cartUI();
      if (!cart) return; // cart type "page": the native POST to /cart/add continues and Shopify opens the cart
      e.preventDefault();
      this.addToCart(cart, this.state.variant, origin);
    }

    linesEvent(id, quantity) {
      const LinesEvent = standard('CartLinesUpdateEvent');
      try {
        const deferred = LinesEvent && LinesEvent.createPromise();
        if (deferred) this.form.dispatchEvent(new LinesEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(id), quantity }], promise: deferred.promise }));
        return deferred || null;
      } catch (e) {
        return null;
      }
    }

    settleLines(deferred, cart, failure) {
      const LinesEvent = standard('CartLinesUpdateEvent');
      try {
        if (!deferred) return;
        if (!failure && LinesEvent && cart && cart.currency) deferred.resolve({ cart: LinesEvent.createCartFromAjaxResponse(cart) });
        else deferred.reject(failure || new Error('Missing cart data'));
      } catch (e) {
        /* analytics only */
      }
    }

    cartErrorEvent(message, code) {
      const ErrorEvent = standard('CartErrorEvent');
      try {
        if (ErrorEvent) this.form.dispatchEvent(new ErrorEvent({ error: message, code }));
      } catch (e) {
        /* analytics only */
      }
    }

    async addToCart(cart, variant, origin) {
      const form = this.form;
      const s = this.strings;
      this.busy = true; // duplicate-submit guard; the button gets aria-disabled, not disabled, so focus stays
      this.stop(this.transient);
      this.transient = 0;
      this.clearMessages();
      form.setAttribute('aria-busy', 'true');
      this.setAtc('loading');

      const body = new FormData(form);
      body.set('id', String(variant.id));
      const quantity = Math.max(1, parseInt(body.get('quantity'), 10) || 1);
      let sections = [];
      try {
        sections = (cart.getSectionsToRender() || []).filter((x) => x && x.id);
        if (cart.setActiveElement) cart.setActiveElement(origin || this.atc);
      } catch (e) {
        /* the cart UI still renders without these */
      }
      if (sections.length) {
        body.set('sections', sections.map((x) => x.id).join(','));
        body.set('sections_url', location.pathname);
      }
      const lines = this.linesEvent(variant.id, quantity);
      const before = Number(variant.cart_quantity) || 0;

      // success | error (definite rejection, shows the server message) | uncertain (timeout, network, 5xx, unreadable reply)
      let outcome = 'uncertain';
      let result = null;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 20000);
      try {
        const res = await fetch(addUrl(form), { method: 'POST', body, credentials: 'same-origin', signal: ctrl.signal, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const text = await res.text();
        try {
          result = JSON.parse(text);
        } catch (e) {
          result = null;
        }
        if (res.status < 500 && (!res.ok || (result && result.status))) outcome = 'error';
        else if (res.ok && result) outcome = 'success';
      } catch (e) {
        outcome = 'uncertain';
      } finally {
        clearTimeout(timer);
      }
      // Before calling it uncertain, check whether the line actually reached the cart.
      let cartJson = null;
      if (outcome === 'uncertain') {
        cartJson = await fetchCart(8000);
        if (inCartCount(cartJson, variant.id) > before) outcome = 'confirmed';
      }
      this.busy = false;
      form.removeAttribute('aria-busy');
      if (outcome === 'success' || outcome === 'confirmed') this.onAdded(cart, sections, outcome === 'success' ? result : null, cartJson, variant, quantity, lines);
      else if (outcome === 'error') this.onAddError(errorText(result) || s.addError || '', result, variant, lines, origin);
      else this.onAddUncertain(lines, origin);
    }

    onAdded(cart, sections, result, cartJson, variant, quantity, lines) {
      setText(this.statusText, this.strings.addedStatus || '');
      setHidden(this.bagLink, false);
      this.flash('success');
      let rendered = false;
      if (result && result.sections && sections.length && sections.every((x) => typeof result.sections[x.id] === 'string')) {
        try {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
          rendered = true;
        } catch (e) {
          rendered = false; // a render failure after a confirmed add is still a successful add
        }
      }
      if (rendered) bump(this);
      publishSafe('cartUpdate', { source: 'product-form', productVariantId: String(variant.id), cartData: result || cartJson });
      this.emit('fsb:pdp:cart-added', { sectionId: this.sectionId, variantId: variant.id, quantity });
      // Follow-up (not blocking the button): cart JSON for analytics, in-cart counts, and the bubble when not rendered.
      this.syncCart(cartJson, !rendered).then((fresh) => {
        this.settleLines(lines, fresh, null);
        if (!rendered && fresh) bump(this);
        if (fresh || !this.state) return;
        variant.cart_quantity = (Number(variant.cart_quantity) || 0) + quantity;
        this.renderQuantity(true);
        this.applyBaseState();
      });
    }

    onAddError(text, result, variant, lines, origin) {
      setText(this.formError, text);
      publishSafe('cartError', {
        source: 'product-form',
        productVariantId: String(variant.id),
        errors: (result && (result.errors || result.description)) || text,
        message: (result && result.message) || text,
      });
      this.cartErrorEvent(text, 'INVALID');
      this.settleLines(lines, null, new Error(text));
      this.flash('error');
      this.emit('fsb:pdp:cart-error', { sectionId: this.sectionId, message: text });
      this.revealForm(origin);
    }

    // Not confirmed either way: keep the button locked (no accidental duplicates) until the page is shown again.
    onAddUncertain(lines, origin) {
      this.uncertain = true;
      setText(this.statusText, this.strings.uncertain || '');
      setHidden(this.bagLink, false);
      this.cartErrorEvent('Network error', 'SERVICE_UNAVAILABLE');
      this.settleLines(lines, null, new Error('Network error'));
      this.setAtc(this.baseState);
      this.emit('fsb:pdp:cart-error', { sectionId: this.sectionId, message: this.strings.uncertain || '' });
      this.revealForm(origin);
    }

    // An add started from the sticky bar shows its result where the message is (the bar then hides itself).
    revealForm(origin) {
      if (!origin) return;
      this.atc.scrollIntoView({ behavior: behaviorFor(this), block: 'center' });
      focusEl(this.atc);
    }

    // Refresh per-variant cart counts (quantity rules, limit) and optionally the header bubble. Resolves with the cart or null.
    async syncCart(known, bubble) {
      const cart = known || (await fetchCart(10000));
      if (!cart || !Array.isArray(cart.items) || !this.ac) return cart && Array.isArray(cart.items) ? cart : null;
      if (bubble) renderBubble(cart);
      this.variants.forEach((v) => (v.cart_quantity = inCartCount(cart, v.id)));
      this.renderQuantity(true);
      this.applyBaseState();
      return cart;
    }
  }

  /* ---------------------------------------------------------------- sticky add-to-cart bar */

  class FsbPdpStickyBar extends Base {
    setup() {
      this.button = qs(this, '[data-fsb-sticky-atc]');
      this.shown = this.forced = false;
      this.desktop = window.matchMedia ? watchMedia(this.getAttribute('data-desktop-query') || '(min-width: 990px)', () => this.update(), this.ac.signal) : null;
      this.on(this.button, 'click', () => {
        if (this.productEl && !this.button.disabled && this.button.getAttribute('aria-disabled') !== 'true') this.emit('fsb:pdp:request-add', { sectionId: this.productSectionId, trigger: this.button });
      });
      const mine = (e) => e.detail && e.detail.sectionId === this.productSectionId;
      this.on(doc, 'fsb:pdp:variant-change', (e) => mine(e) && this.render(e.detail));
      this.on(doc, 'fsb:pdp:atc-state', (e) => {
        if (!mine(e)) return;
        this.paint(e.detail.state, e.detail.locked);
        this.update();
      });
      this.on(doc, 'fsb:pdp:cart-error', () => this.update());
      this.on(doc, 'shopify:section:load', () => this.wait(() => this.bind(), 0));
      const force = (on) => (e) => {
        if (!e.detail || e.detail.sectionId !== this.getAttribute('data-section-id')) return;
        this.forced = on; // theme editor: show the bar while its section is selected
        this.update();
      };
      this.on(doc, 'shopify:section:select', force(true));
      this.on(doc, 'shopify:section:deselect', force(false));
      if ('ResizeObserver' in window) (this.sizeObserver = new ResizeObserver(() => this.shown && this.setOffset())).observe(this);
      this.bind();
    }

    teardown() {
      if (this.observer) this.observer.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      html.classList.remove('fsb-sticky-on');
      html.style.removeProperty('--fsb-sticky-offset');
    }

    setOffset() {
      html.style.setProperty('--fsb-sticky-offset', `${this.offsetHeight}px`);
    }

    // The product root is the first fsb-pdp-product on the page (data-product-section-id is left empty by the section).
    bind() {
      if (this.observer) this.observer.disconnect();
      const wanted = this.getAttribute('data-product-section-id');
      const roots = qsa(doc, 'fsb-pdp-product');
      const root = (this.productEl = wanted
        ? roots.find((r) => r.getAttribute('data-section-id') === wanted)
        : roots.find((r) => r.getAttribute('data-product-id') === this.getAttribute('data-product-id')) || roots[0]);
      if (!root) return this.update();
      this.productSectionId = root.getAttribute('data-section-id') || '';
      this.strings = stringsFor(root);
      this.data = productDataFor(root) || {};
      this.sentinel = qs(root, '[data-fsb-atc-sentinel]');
      this.errorEl = qs(root, '[data-fsb-form-error]');
      this.footer = qs(doc, '.shopify-section-group-footer-group') || qs(doc, 'footer');
      this.above = this.footerIn = this.errorIn = false;
      if (root.snapshot) {
        const snap = root.snapshot();
        this.canAdd = snap.canAdd;
        if (this.imageId === undefined && snap.variant && snap.variant.featured_media_id != null) this.imageId = String(snap.variant.featured_media_id);
        this.render(snap);
      }
      if (!this.sentinel || !('IntersectionObserver' in window)) return this.update();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.target === this.sentinel) this.above = !en.isIntersecting && en.boundingClientRect.bottom <= (en.rootBounds ? en.rootBounds.top : 0);
          else if (en.target === this.footer) this.footerIn = en.isIntersecting;
          else this.errorIn = en.isIntersecting;
        });
        this.update();
      });
      [this.sentinel, this.footer, this.errorEl].forEach((el) => el && this.observer.observe(el));
    }

    // Visible when the main CTA is above the viewport, the footer is not in view, no visible form error would be
    // covered, and the bar is enabled for the current breakpoint.
    update() {
      const enabled = this.getAttribute(this.desktop && this.desktop.matches ? 'data-enable-desktop' : 'data-enable-mobile') === 'true';
      const error = this.errorIn && this.errorEl && this.errorEl.textContent.trim();
      const show = Boolean(this.productEl && this.productEl.isConnected && this.canAdd !== false && enabled) && (this.forced || (this.above && !this.footerIn && !error));
      if (show) this.show();
      else this.hide();
    }

    show() {
      this.stop(this.leaveTimer);
      this.removeAttribute('data-fsb-leaving');
      if (this.shown) return;
      this.shown = true;
      this.hidden = false;
      this.removeAttribute('inert');
      this.setOffset();
      html.classList.add('fsb-sticky-on');
    }

    hide() {
      if (!this.shown) return;
      const hadFocus = this.contains(doc.activeElement);
      const finish = () => {
        this.hidden = true;
        this.removeAttribute('data-fsb-leaving');
      };
      this.shown = false;
      html.classList.remove('fsb-sticky-on');
      this.setAttribute('inert', '');
      if (this.getAttribute('data-fsb-motion') === 'true' && !reduceMotion) {
        this.setAttribute('data-fsb-leaving', ''); // CSS exit animation, then [hidden]
        this.leaveTimer = this.wait(finish, 240);
      } else finish();
      if (hadFocus) focusEl(qs(this.productEl, '[data-fsb-atc]'));
    }

    render(d) {
      const summary = qs(this, '[data-fsb-sticky-summary]');
      const price = qs(this, '[data-fsb-sticky-price]');
      const text = this.data.hasOnlyDefaultVariant ? '' : (d.selectedOptions || []).filter((x) => x !== null).join(' · ');
      setText(summary, text);
      setHidden(summary, !text);
      // Always paint the setting-aware price_html: the server fallback ignores the Price block's compare-at setting.
      const key = d.variant ? String(d.variant.id) : 'product';
      if (price && this.priceKey !== key) {
        price.innerHTML = (d.variant ? d.variant.price_html : this.data.priceHtml) || '';
      }
      this.priceKey = key;
      this.renderImage(d.variant);
      if (d.atcState) this.paint(d.atcState, d.locked);
      else if (this.productEl && this.productEl.snapshot) this.paint(this.productEl.atcState, this.productEl.uncertain);
    }

    paint(state, locked) {
      if (this.strings && this.strings.addToCart) paintButton(this.button, qs(this, '[data-fsb-sticky-atc-label]'), state || 'idle', Boolean(locked), this.strings);
    }

    // Swap the thumbnail to the variant's image, reusing the gallery image URL at 64/128 px.
    renderImage(v) {
      const img = qs(this, '[data-fsb-sticky-image]');
      if (!img || !v || v.featured_media_id == null || this.imageId === String(v.featured_media_id)) return;
      const slide = qsa(this.productEl, '[data-fsb-gallery-track] > [data-fsb-media-id]').find((s) => mediaId(s) === String(v.featured_media_id));
      const src = slide && qs(slide, 'img') ? qs(slide, 'img').getAttribute('src') : '';
      if (!src) return;
      try {
        const at = (w) => {
          const url = new URL(src, location.href);
          url.searchParams.set('width', String(w));
          url.searchParams.delete('height');
          url.searchParams.delete('crop');
          return url.href;
        };
        img.srcset = `${at(64)} 64w, ${at(128)} 128w`;
        img.src = at(128);
        this.imageId = String(v.featured_media_id);
      } catch (e) {
        /* keep the current thumbnail */
      }
    }
  }

  /* ---------------------------------------------------------------- recommendations and recently viewed */

  // Run once when `target` comes within 600px of the viewport. A [hidden] host has no box, so its section wrapper is watched.
  const whenNear = (target, fn) => {
    if (!('IntersectionObserver' in window)) return fn(), null;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      fn();
    }, { rootMargin: '600px 0px' });
    io.observe(target);
    return io;
  };
  const parseHTML = async (res) => new DOMParser().parseFromString(await res.text(), 'text/html');

  class FsbPdpRecommendations extends Base {
    setup() {
      this.list = qs(this, '[data-fsb-recs-list]');
      this.track = qs(this, '[data-fsb-recs-track]');
      this.prev = qs(this, '[data-fsb-recs-prev]');
      this.next = qs(this, '[data-fsb-recs-next]');
      this.min = Math.max(1, parseInt(this.getAttribute('data-min'), 10) || 1);
      if (this.track && this.prev && this.next) {
        this.on(this.prev, 'click', () => this.scrollByItem(-1));
        this.on(this.next, 'click', () => this.scrollByItem(1));
        this.on(this.track, 'scroll', () => this.scheduleNav(), { passive: true });
        if ('ResizeObserver' in window) (this.navObserver = new ResizeObserver(() => this.scheduleNav())).observe(this.track);
      }
      const url = this.getAttribute('data-url');
      if (url && this.list) this.io = whenNear(this.parentElement || this, () => this.load(url));
      else this.finish();
    }

    teardown() {
      if (this.io) this.io.disconnect();
      if (this.navObserver) this.navObserver.disconnect();
      if (this.request) this.request.abort();
      cancelAnimationFrame(this.navFrame);
      this.navFrame = 0;
    }

    // Shopify recommendations (section rendering): replace the server list only when at least data-min products came back.
    async load(url) {
      const ctrl = (this.request = new AbortController());
      try {
        const res = await fetch(url, { signal: ctrl.signal, credentials: 'same-origin' });
        const limit = parseInt(this.getAttribute('data-limit'), 10) || 99;
        const productId = this.getAttribute('data-product-id');
        const items = res.ok ? qsa(await parseHTML(res), '[data-fsb-recs-list] [data-fsb-rec-item]').filter((i) => i.getAttribute('data-fsb-product-id') !== productId).slice(0, limit) : [];
        if (items.length >= this.min && this.ac) this.list.replaceChildren(...items.map((i) => doc.adoptNode(i)));
      } catch (e) {
        /* keep the server-rendered products */
      }
      this.request = null;
      if (this.ac) this.finish();
    }

    // Hidden below data-min products, except in the theme editor (which shows a placeholder note).
    finish() {
      const count = qsa(this.list, '[data-fsb-rec-item]').length;
      if (count >= this.min) this.hidden = false;
      else if (!this.hasAttribute('data-fsb-editor')) this.hidden = true;
      this.scheduleNav();
    }

    // Arrows appear only when the row overflows (below 990px) and are disabled at each end.
    scheduleNav() {
      if (!this.track || !this.prev || !this.next || this.navFrame) return;
      this.navFrame = requestAnimationFrame(() => {
        const overflow = scrolls(this.track);
        const x = Math.abs(this.track.scrollLeft);
        this.navFrame = 0;
        setHidden(this.prev, !overflow);
        setHidden(this.next, !overflow);
        if (!overflow) return;
        this.setDisabled(this.prev, x <= 1);
        this.setDisabled(this.next, x >= this.track.scrollWidth - this.track.clientWidth - 1);
      });
    }

    setDisabled(button, off) {
      if (button.disabled === off) return;
      const other = button === this.prev ? this.next : this.prev;
      if (off && doc.activeElement === button && !other.disabled) focusEl(other); // never strand keyboard focus
      button.disabled = off;
    }

    scrollByItem(dir) {
      const item = qs(this.list, '[data-fsb-rec-item]');
      const gap = this.list ? parseFloat(getComputedStyle(this.list).columnGap) || 0 : 0;
      this.track.scrollBy({ left: dir * (item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8), behavior: behaviorFor(this.track) });
    }
  }

  const readHandles = (key) => {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h && h.length < 256 && !/[/?#]/.test(h)) : [];
    } catch (e) {
      return [];
    }
  };
  const writeHandles = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* storage blocked or full: the section simply stays hidden */
    }
  };

  class FsbPdpRecentlyViewed extends Base {
    setup() {
      const handle = this.getAttribute('data-product-handle') || '';
      this.key = this.getAttribute('data-storage-key') || 'fsb:recently-viewed';
      this.list = qs(this, '[data-fsb-recent-list]');
      this.candidates = readHandles(this.key).filter((h) => h !== handle);
      // Record this product: newest first, de-duplicated, capped at data-max-stored.
      if (handle) writeHandles(this.key, [handle].concat(this.candidates).slice(0, parseInt(this.getAttribute('data-max-stored'), 10) || 12));
      if (this.candidates.length && this.list) this.io = whenNear(this.parentElement || this, () => this.load());
    }

    teardown() {
      if (this.io) this.io.disconnect();
      if (this.request) this.request.abort();
    }

    // Fetch one card per handle (in order) until data-limit cards exist; forget handles that no longer exist (404).
    async load() {
      const ctrl = (this.request = new AbortController());
      const limit = Math.max(1, parseInt(this.getAttribute('data-limit'), 10) || 4);
      const root = (this.getAttribute('data-root') || '/').replace(/\/?$/, '/');
      const section = encodeURIComponent(this.getAttribute('data-card-section') || 'fsb-pdp-card-render');
      const fetchCard = async (handle) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${section}`, { signal: ctrl.signal, credentials: 'same-origin' });
          if (res.status === 404) return 404;
          const wrapper = res.ok ? qs(await parseHTML(res), '[data-fsb-card-render]') : null;
          return qs(wrapper, '.fsb-product') || (wrapper && wrapper.firstElementChild);
        } catch (e) {
          return null;
        }
      };
      const cards = [];
      const gone = [];
      for (let i = 0; cards.length < limit && i < this.candidates.length && !ctrl.signal.aborted; ) {
        const batch = this.candidates.slice(i, i + limit - cards.length);
        i += batch.length;
        (await Promise.all(batch.map(fetchCard))).forEach((card, n) => (card === 404 ? gone.push(batch[n]) : card && cards.push(card)));
      }
      this.request = null;
      if (gone.length) writeHandles(this.key, readHandles(this.key).filter((h) => gone.indexOf(h) === -1));
      if (!this.ac || !cards.length) return;
      const tpl = qs(this, 'template[data-fsb-recent-item-template]');
      cards.forEach((card) => {
        const item = tpl && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : doc.createElement('li');
        item.appendChild(doc.adoptNode(card));
        this.list.appendChild(item);
      });
      this.hidden = false;
    }
  }

  /* ---------------------------------------------------------------- registration */

  // Leaf elements first so fsb-pdp-product finds them upgraded; the sticky bar after the product root it reads.
  define('fsb-pdp-video', FsbPdpVideo);
  define('fsb-pdp-gallery', FsbPdpGallery);
  define('fsb-pdp-lightbox', FsbPdpLightbox);
  define('fsb-pdp-modal', FsbPdpModal);
  define('fsb-pdp-accordion', FsbPdpAccordion);
  define('fsb-pdp-quantity', FsbPdpQuantity);
  define('fsb-pdp-share', FsbPdpShare);
  define('fsb-pdp-product', FsbPdpProduct);
  define('fsb-pdp-sticky-bar', FsbPdpStickyBar);
  define('fsb-pdp-recommendations', FsbPdpRecommendations);
  define('fsb-pdp-recently-viewed', FsbPdpRecentlyViewed);

  window.FsbPdp = Object.freeze({ version: VERSION });
})();
